import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { RELATION_TYPES } from "@madamuh/domain";
import type { GuardianLinkRequestDto, RelationType } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createGuardianLinkRequest } from "../../shared/api/guardian";
import { describeRelationType, describeStatus } from "../../shared/lib/uiLabels";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { PageScaffold } from "../../shared/ui/PageScaffold";

export function LinkStudentRequestPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [requestedRelationType, setRequestedRelationType] = useState<RelationType>("parent");
  const [studentName, setStudentName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [supportingInfo, setSupportingInfo] = useState("");
  const [requestRecord, setRequestRecord] = useState<GuardianLinkRequestDto | null>(null);
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitState("submitting");
    setSubmitError(null);

    try {
      const createdRequest = await createGuardianLinkRequest(tenantSlug, {
        requestedRelationType,
        studentName,
        birthDate: birthDate || null,
        supportingInfo: supportingInfo || null
      });

      setRequestRecord(createdRequest);
      setSubmitState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setSubmitState("error");
      setSubmitError(getProtectedApiErrorMessage(error, "申請の送信に失敗しました"));
    }
  }

  return (
    <PageScaffold
      title="生徒連携申請"
      description="招待を持っていない場合の保護者紐付け申請です。申請後、管理者が確認するまで生徒情報は表示されません。"
      links={[
        { to: "/t/:tenantSlug/link-student", label: "招待入力へ戻る" },
        { to: "/t/:tenantSlug/entry", label: "利用開始へ戻る" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}

        {isSessionReady ? (
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
              申請送信だけではまだ生徒との紐付けは作成されません。管理者が承認した後に、ホーム画面で予定や生徒情報を確認できるようになります。
            </p>
            <label style={{ display: "grid", gap: 4 }}>
              <span>続柄</span>
              <select
                value={requestedRelationType}
                onChange={(event) => setRequestedRelationType(event.target.value as RelationType)}
              >
                {RELATION_TYPES.map((relationType) => (
                  <option key={relationType} value={relationType}>
                    {describeRelationType(relationType)}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>生徒名</span>
              <input
                value={studentName}
                onChange={(event) => setStudentName(event.target.value)}
                placeholder="例: 山田 花子"
                required
              />
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>生年月日</span>
              <input value={birthDate} onChange={(event) => setBirthDate(event.target.value)} type="date" />
            </label>

            <label style={{ display: "grid", gap: 4 }}>
              <span>補足情報</span>
              <textarea
                value={supportingInfo}
                onChange={(event) => setSupportingInfo(event.target.value)}
                rows={4}
                placeholder="兄弟姉妹名や所属クラスなど"
              />
            </label>

            <button type="submit" disabled={submitState === "submitting"}>
              {submitState === "submitting" ? "送信中..." : "申請を送信する"}
            </button>
          </form>
        ) : null}

        {submitError ? <p>{submitError}</p> : null}

        {requestRecord ? (
          <div style={{ display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>申請を登録しました。</p>
            <p style={{ margin: 0 }}>申請ID: {requestRecord.requestId}</p>
            <p style={{ margin: 0 }}>ステータス: {describeStatus(requestRecord.status)}</p>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
              管理者が承認するまで生徒情報はまだ表示されません。
            </p>
            <p style={{ margin: 0 }}>
              管理者の確認先: <Link to={`/t/${tenantSlug}/teacher/admin/guardian-link-requests`}>申請一覧を見る</Link>
            </p>
          </div>
        ) : null}
      </div>
    </PageScaffold>
  );
}
